import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Sequence — vue React staircase + debrief (cyanotype Wave M2-game-chrome).
 */
import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DebriefShell, useGameShellFromContext } from '@labrain/game-shell';
import { COLOR_PALETTE, staircaseConfig, variantForLevel } from './params';
const STAGE_SIZE = 280;
const COLOR_DOT_SIZE = 80;
function Stage({ mode, currentItem }) {
    return (_jsx("div", { className: "flex items-center justify-center", style: {
            width: STAGE_SIZE,
            height: STAGE_SIZE,
            background: '#1a1a1a',
        }, children: currentItem === null ? (_jsx("span", { className: "font-display text-[40px] text-cyan-surface opacity-30", children: "\u00B7" })) : mode === 'digits' ? (_jsx("span", { className: "font-display text-cyan-surface", style: { fontSize: 96, lineHeight: 1 }, children: currentItem })) : (_jsx("span", { "aria-hidden": true, style: {
                display: 'inline-block',
                width: COLOR_DOT_SIZE,
                height: COLOR_DOT_SIZE,
                borderRadius: '50%',
                background: COLOR_PALETTE[currentItem % COLOR_PALETTE.length].bg,
                boxShadow: '0 0 36px rgba(255,255,255,0.18)',
            } })) }));
}
function DigitKeypad({ onPick, disabled }) {
    const { t } = useTranslation('games');
    // Layout 3x4 : 1 2 3 / 4 5 6 / 7 8 9 / _ 0 _
    const rows = [
        [1, 2, 3],
        [4, 5, 6],
        [7, 8, 9],
        [null, 0, null],
    ];
    return (_jsx("div", { className: "grid gap-s2", style: { gridTemplateColumns: 'repeat(3, 64px)' }, children: rows.flat().map((d, i) => {
            if (d === null)
                return _jsx("div", {}, `spacer-${i}`);
            return (_jsx("button", { type: "button", onClick: () => !disabled && onPick(d), disabled: disabled, "aria-label": t('sequence.digit_aria', { digit: d }), className: [
                    'flex h-[64px] w-[64px] items-center justify-center font-display text-[26px] font-medium',
                    'transition-all duration-feedback ease-soft-wind',
                    disabled
                        ? 'cursor-not-allowed bg-cyan-bg text-cyan-text-secondary opacity-50'
                        : 'cursor-pointer bg-cyan-core text-cyan-surface hover:bg-cyan-network active:scale-95',
                ].join(' '), children: d }, `digit-${d}`));
        }) }));
}
function ColorKeypad({ onPick, disabled }) {
    const { t } = useTranslation('games');
    return (_jsx("div", { className: "grid gap-s2", style: { gridTemplateColumns: 'repeat(4, 56px)' }, children: COLOR_PALETTE.map((c, idx) => (_jsx("button", { type: "button", onClick: () => !disabled && onPick(idx), disabled: disabled, "aria-label": t('sequence.color_aria', { name: c.label }), className: [
                'h-[56px] w-[56px] rounded-full',
                'transition-all duration-feedback ease-soft-wind',
                disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:scale-110 active:scale-95',
            ].join(' '), style: { background: c.bg, boxShadow: '0 2px 6px rgba(0,0,0,0.18)' } }, c.key))) }));
}
function PickedTrail({ mode, picked, truth, showFeedback }) {
    if (picked.length === 0) {
        return _jsx("div", { className: "h-[28px]" });
    }
    return (_jsx("div", { className: "flex items-center justify-center gap-s2 h-[28px]", children: picked.map((p, i) => {
            let bg = 'bg-cyan-bg';
            let text = 'text-cyan-text';
            if (showFeedback && truth) {
                const matches = truth[i] === p;
                bg = matches ? 'bg-cyan-core' : 'bg-cyan-media';
                text = 'text-cyan-surface';
            }
            return mode === 'digits' ? (_jsx("span", { className: `flex h-[24px] w-[24px] items-center justify-center font-display text-[14px] font-medium ${bg} ${text}`, children: p }, `trail-${i}`)) : (_jsx("span", { "aria-hidden": true, className: [
                    'inline-block rounded-full',
                    showFeedback && truth && truth[i] !== p ? 'ring-2 ring-cyan-media' : '',
                ].join(' '), style: {
                    width: 20,
                    height: 20,
                    background: COLOR_PALETTE[p % COLOR_PALETTE.length].bg,
                } }, `trail-${i}`));
        }) }));
}
export function SeqStaircaseView({ adapter, level, onAbort }) {
    const { t } = useTranslation(['games', 'common']);
    const subscribe = useCallback((notify) => adapter.subscribe(() => notify()), [adapter]);
    const getSnapshot = useCallback(() => adapter.getState(), [adapter]);
    const adapterState = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    const shell = useGameShellFromContext();
    const variant = variantForLevel(level);
    const handlePick = useCallback((item) => adapter.handlePick(item), [adapter]);
    const handleClear = useCallback(() => adapter.handleClearLast(), [adapter]);
    const trialDisplay = shell.snapshot.staircase.trialIdx + 1;
    const currentItem = useMemo(() => {
        if (adapterState.subPhase === 'presenting' &&
            adapterState.stim &&
            adapterState.currentItemIdx >= 0 &&
            adapterState.currentItemIdx < adapterState.stim.items.length) {
            return adapterState.stim.items[adapterState.currentItemIdx];
        }
        return null;
    }, [adapterState.subPhase, adapterState.stim, adapterState.currentItemIdx]);
    const showFeedback = adapterState.subPhase === 'feedback';
    const span = adapterState.stim?.spanLength ?? 0;
    const picksSoFar = adapterState.picked.length;
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s5 py-s4", children: [_jsxs("div", { className: "flex w-full items-center justify-between text-[12px] text-cyan-text-secondary", children: [_jsx("span", { className: "font-mono uppercase tracking-[0.20em] text-cyan-text-faint", children: t('common:labels.trial_count', {
                            current: Math.min(trialDisplay, staircaseConfig.maxTrials),
                            total: staircaseConfig.maxTrials,
                        }) }), _jsxs("span", { className: "font-mono text-[11px]", children: [variant.mode === 'digits' ? '0-9' : 'colors', " | n=", span] }), _jsx("button", { type: "button", onClick: () => {
                            shell.abort('user');
                            onAbort();
                        }, className: "border border-cyan-border-faint px-s3 py-s1 text-[11px] text-cyan-text-secondary hover:border-cyan-border", children: t('common:actions.stop') })] }), _jsxs("div", { className: "flex h-[28px] items-center justify-center text-[14px]", children: [adapterState.subPhase === 'fixation' && (_jsx("span", { className: "text-cyan-text-secondary", children: "\u2026" })), adapterState.subPhase === 'pre_sequence' && (_jsx("span", { className: "font-display text-cyan-network", children: t('sequence.phases.encoding') })), adapterState.subPhase === 'presenting' && (_jsx("span", { className: "font-display text-cyan-network", children: t('sequence.phases.encoding') })), adapterState.subPhase === 'awaiting_input' && (_jsx("span", { className: "text-cyan-text-secondary", children: t('sequence.phases.input', { picks: picksSoFar, n: span }) })), adapterState.subPhase === 'feedback' && (_jsx("span", { className: [
                            'font-display text-[18px] font-medium',
                            adapterState.feedback === 'correct'
                                ? 'text-cyan-core'
                                : 'text-cyan-media',
                        ].join(' '), children: adapterState.feedback === 'correct'
                            ? `✓ ${t('sequence.feedback.correct')}`
                            : `✗ ${t('sequence.feedback.incorrect')}` }))] }), _jsx(Stage, { mode: variant.mode, currentItem: currentItem }), _jsx(PickedTrail, { mode: variant.mode, picked: adapterState.picked, truth: adapterState.stim?.items, showFeedback: showFeedback }), variant.mode === 'digits' ? (_jsx(DigitKeypad, { onPick: handlePick, disabled: adapterState.subPhase !== 'awaiting_input' })) : (_jsx(ColorKeypad, { onPick: handlePick, disabled: adapterState.subPhase !== 'awaiting_input' })), _jsx("div", { className: "flex items-center gap-s3", children: _jsx("button", { type: "button", onClick: handleClear, disabled: adapterState.subPhase !== 'awaiting_input' || picksSoFar === 0, className: [
                        'border border-cyan-border-faint px-s4 py-s1 text-[11px] text-cyan-text-secondary',
                        adapterState.subPhase !== 'awaiting_input' || picksSoFar === 0
                            ? 'cursor-not-allowed opacity-40'
                            : 'hover:border-cyan-border',
                    ].join(' '), children: t('sequence.eraser') }) }), _jsx("div", { className: "text-[11px] text-cyan-text-secondary", children: t('sequence.footer_hint') })] }));
}
function recommendKey(threshold) {
    if (threshold !== null && threshold >= 7) {
        return 'sequence.debrief.recommend_excellent';
    }
    if (threshold !== null && threshold >= 5) {
        return 'sequence.debrief.recommend_good';
    }
    return 'sequence.debrief.recommend_keep';
}
export function SeqDebriefView({ level: _level, onAbort: _onAbort }) {
    const { t } = useTranslation(['games', 'common']);
    const shell = useGameShellFromContext();
    const staircase = shell.snapshot.staircase;
    const threshold = staircase.thresholdEstimate;
    const trialsCompleted = staircase.trialIdx;
    const trialsCorrect = staircase.trialLog.filter((tt) => tt.response.correct).length;
    const accuracy = trialsCompleted === 0
        ? 0
        : Math.round((trialsCorrect / trialsCompleted) * 100);
    const bestSpan = useMemo(() => {
        let best = 0;
        for (const tt of staircase.trialLog) {
            if (tt.response.correct && tt.value > best)
                best = tt.value;
        }
        return Math.round(best);
    }, [staircase]);
    const fallback = !!staircase.fallback;
    const thresholdLabel = threshold === null ? '—' : threshold.toFixed(1).replace(/\.0$/, '');
    const replay = () => {
        window.location.reload();
    };
    return (_jsx(DebriefShell, { eyebrow: t('common:eyebrow.session_done'), title: t('sequence.debrief.title'), scoreLabel: t('sequence.debrief.score_label'), scoreValue: thresholdLabel, fallbackBadge: fallback, metrics: [
            { label: t('common:metrics.trials'), value: trialsCompleted },
            { label: t('common:metrics.correct_pct'), value: `${accuracy}%` },
            { label: t('sequence.debrief.metric_best_span'), value: bestSpan },
        ], recommendation: t(recommendKey(threshold)), scientificCaveat: t('sequence.debrief.scientific_caveat'), ctaReplay: _jsx("button", { type: "button", onClick: replay, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: t('common:actions.replay') }), ctaBack: _jsx(Link, { to: "/library", className: "inline-flex h-12 w-full items-center justify-center border border-cyan-border bg-cyan-surface font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-text transition active:scale-[0.985] hover:bg-cyan-bg", children: t('common:actions.choose_another') }) }));
}
//# sourceMappingURL=staircaseView.js.map